/**
 * 컨트롤러 표시명.
 * - 조명제어기: "12채널 조명제어기 M24 (V4)" 형식 (모델코드 + 버전)
 * - 라우터/온도조절기: 모델코드 포함 고정 라벨
 * - 그 외 → Product.modelName (없으면 fallback)
 */
export const CONTROLLER_DISPLAY_NAME_V41 = '12채널 조명제어기 M24 (V4.1)';
export const CONTROLLER_DISPLAY_NAME_V4 = '12채널 조명제어기 M24 (V4)';
export const CONTROLLER_DISPLAY_NAME_M22 = '12채널 조명제어기 M22 (V3)';
export const CONTROLLER_DISPLAY_NAME_M02 = '12채널 조명제어기 M02 (V2)';
export const CONTROLLER_DISPLAY_NAME_M31 = '4채널 조명제어기 M31';
export const CONTROLLER_DISPLAY_NAME_M11 = '통신모듈 라우터 M11';
export const CONTROLLER_DISPLAY_NAME_T02 = '온도조절기 75+ T02';
export const CONTROLLER_DISPLAY_NAME_T01 = '온도조절기 75 T01';

function controllerIdAndModel(controllerOrId) {
  if (controllerOrId == null) {
    return { id: '', model: '' };
  }
  if (typeof controllerOrId === 'string') {
    const id = controllerOrId;
    return {
      id,
      model: id.includes('_') ? id.split('_')[0] : id,
    };
  }
  const id = String(controllerOrId._id || controllerOrId.id || '');
  const model = String(
    controllerOrId.model || (id.includes('_') ? id.split('_')[0] : '') || '',
  );
  return { id, model };
}

/** M22(12채널 V3) */
export function isM22Controller(controllerOrId) {
  const { id, model } = controllerIdAndModel(controllerOrId);
  return model === 'M22' || id.startsWith('M22_');
}

/**
 * M24 V4 이상 여부.
 * - model/시리얼이 M24 이고
 * - hwRevision 미보고이거나 "4.0"/"4.1"(≥4.0)
 */
export function isM24V4OrAbove(controller) {
  if (!controller) return false;
  const { id, model } = controllerIdAndModel(controller);
  if (model !== 'M24' && !id.startsWith('M24_')) {
    return false;
  }
  const rev = controller.hwRevision;
  if (rev == null || rev === '') {
    return true;
  }
  const n = parseFloat(String(rev));
  return !Number.isNaN(n) && n >= 4.0;
}

/** M31(4채널) */
export function isM31Controller(controllerOrId) {
  const { id, model } = controllerIdAndModel(controllerOrId);
  return model === 'M31' || id.startsWith('M31_');
}

/**
 * 일괄소등·센서연동·조명버튼 매핑 UI 지원.
 * M24 V4 이상, M22(V3), M02(V2), M31(4ch)를 포함한다.
 */
export function supportsSensorAndAoLink(controller) {
  return (
    isM24V4OrAbove(controller) ||
    isM22Controller(controller) ||
    isM02Controller(controller) ||
    isM31Controller(controller)
  );
}

/**
 * 조명 일괄 제어 MQTT cmd `cs` 지원 여부.
 * - M24 / M22(V3) / M02 / M31: cs 배치 (그룹 ON/OFF·밝기, 포트별 bright/color 유지)
 * - 그 외 구형: 개별 cmd `c`
 * 전제: M22/M02는 cs·vc 펌웨어(예: M22 10122+, M02 10305+) 현장 배포, M31은 10405+
 */
export function supportsLampCmdCs(controllerOrId) {
  if (controllerOrId == null) return false;
  const { id, model } = controllerIdAndModel(controllerOrId);
  return (
    model === 'M24' ||
    id.startsWith('M24_') ||
    isM22Controller(controllerOrId) ||
    isM02Controller(controllerOrId) ||
    isM31Controller(controllerOrId)
  );
}

/** M02(12채널 V2) — 물리 포트 라벨이 펌웨어 인덱스와 역순 */
export function isM02Controller(controllerOrId) {
  if (controllerOrId == null) return false;
  if (typeof controllerOrId === 'string') {
    return controllerOrId === 'M02' || controllerOrId.startsWith('M02_');
  }
  const id = String(controllerOrId._id || controllerOrId.id || '');
  const model = String(
    controllerOrId.model || (id.includes('_') ? id.split('_')[0] : '') || '',
  );
  return model === 'M02' || id.startsWith('M02_');
}

export const LAMP_CHANNEL_COUNT_M02 = 12;
export const LAMP_CHANNEL_COUNT_M31 = 4;

/** 컨트롤러별 조명 채널 수 (센서 연동 UI 등) */
export function lampChannelCount(controllerOrId) {
  if (isM31Controller(controllerOrId)) return LAMP_CHANNEL_COUNT_M31;
  if (isM02Controller(controllerOrId)) return LAMP_CHANNEL_COUNT_M02;
  return 12;
}

/**
 * 내부 0-based 포트 → 앱 표시용 1-based 번호.
 * M02 V2: 물리 라벨이 역순이므로 12↔1 로 뒤집어 표시 (0→12, 11→1).
 */
export function toDisplayLampPort(
  internalPort,
  controllerOrId,
  channelCount = LAMP_CHANNEL_COUNT_M02,
) {
  const p = Number(internalPort);
  if (!Number.isInteger(p) || p < 0) return internalPort;
  if (isM02Controller(controllerOrId)) {
    return channelCount - p;
  }
  return p + 1;
}

/**
 * 센서 연동 채널: 펌웨어 1-based (cmd ui sensor_links) → UI 표시 번호.
 * M02: 실크 라벨과 맞춤 (펌웨어 12 → 표시 1, 펌웨어 1 → 표시 12).
 */
export function toDisplaySensorChannel(
  firmwareCh1,
  controllerOrId,
  channelCount = LAMP_CHANNEL_COUNT_M02,
) {
  const ch = Number(firmwareCh1);
  if (!Number.isInteger(ch) || ch < 1 || ch > channelCount) return firmwareCh1;
  if (isM02Controller(controllerOrId)) {
    return channelCount + 1 - ch;
  }
  return ch;
}

/**
 * UI 표시 번호 → 펌웨어 1-based sensor_links 값.
 * M02: 표시 1(실크1) → 펌웨어 12.
 */
export function fromDisplaySensorChannel(
  displayCh,
  controllerOrId,
  channelCount = LAMP_CHANNEL_COUNT_M02,
) {
  const ch = Number(displayCh);
  if (!Number.isInteger(ch) || ch < 1 || ch > channelCount) return displayCh;
  if (isM02Controller(controllerOrId)) {
    return channelCount + 1 - ch;
  }
  return ch;
}

/**
 * 앱 표시 1-based 번호 → 내부 0-based 포트.
 * M02: 실크 1 → 내부 11, 실크 12 → 내부 0.
 */
export function fromDisplayLampPort(displayPort, controllerOrId) {
  const count = lampChannelCount(controllerOrId);
  const d = Number(displayPort);
  if (!Number.isInteger(d) || d < 1 || d > count) return null;
  if (isM02Controller(controllerOrId)) return count - d;
  return d - 1;
}

/** ports 배열/단일값을 "1, 2" 형태 표시 문자열로 */
export function formatDisplayLampPorts(
  ports,
  controllerOrId,
  channelCount = LAMP_CHANNEL_COUNT_M02,
) {
  const list = Array.isArray(ports) ? ports : ports != null ? [ports] : [];
  return list
    .map((p) => toDisplayLampPort(p, controllerOrId, channelCount))
    .join(', ');
}

export function findControllerInZone(controllers, controllerId) {
  if (!controllerId || !controllers?.length) return null;
  const id = String(controllerId);
  return (
    controllers.find((c) => {
      const cid = typeof c === 'string' ? c : c?._id || c?.id;
      return cid != null && String(cid) === id;
    }) || null
  );
}

/**
 * 시리얼 + (사용자 이름). 이름 없으면 시리얼만.
 * 예: "M24_8857… (솔거대림)"
 */
export function formatControllerSerialWithName(controllerOrId) {
  if (controllerOrId == null) return '';
  if (typeof controllerOrId === 'string') return controllerOrId;
  const id = String(controllerOrId._id || controllerOrId.id || '');
  if (!id) return '';
  const name =
    typeof controllerOrId.name === 'string' ? controllerOrId.name.trim() : '';
  return name ? `${id} (${name})` : id;
}

export function resolveControllerDisplayName(controller, products = [], fallback = '조명제어기') {
  const { id, model } = controllerIdAndModel(controller);

  if (model === 'M24' || id.startsWith('M24_')) {
    if (controller?.hwRevision === '4.1') {
      return CONTROLLER_DISPLAY_NAME_V41;
    }
    return CONTROLLER_DISPLAY_NAME_V4;
  }
  if (isM22Controller(controller)) {
    return CONTROLLER_DISPLAY_NAME_M22;
  }
  if (isM02Controller(controller)) {
    return CONTROLLER_DISPLAY_NAME_M02;
  }
  if (isM31Controller(controller)) {
    return CONTROLLER_DISPLAY_NAME_M31;
  }
  if (model === 'M11' || id.startsWith('M11_')) {
    return CONTROLLER_DISPLAY_NAME_M11;
  }
  if (model === 'M91' || id.startsWith('M91_')) {
    return '가상스위치 제어기';
  }
  const tempModel = String(model || controller?.model || '').trim().toUpperCase();
  if (tempModel === 'T02') {
    return CONTROLLER_DISPLAY_NAME_T02;
  }
  if (tempModel === 'T01') {
    return CONTROLLER_DISPLAY_NAME_T01;
  }
  if (controller?.model) {
    const product = (products || []).find((p) => p.model === controller.model);
    if (product?.modelName) {
      return product.modelName;
    }
    return controller.model;
  }
  if (controller?._id) {
    const parts = String(controller._id).split('_');
    return parts.length > 1 ? '조명제어기' : '온도조절기';
  }
  return fallback;
}
